import React, { useState } from "react";
import PropTypes from "prop-types";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useGlobal } from "../../GlobalContext";
import { useHistory } from "react-router-dom";

import GridContainer from "../../components/Grid/GridContainer.js";
import Grid from "@material-ui/core/Grid";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import CoreButton from "@material-ui/core/Button";
import Button from "../../components/CustomButtons/Button.js";

import MetaInfo from "./MetaInfo.js";
import CertificateInfo from "./CertificateInfo.js";
import CustomTableInfo from "./CustomTableInfo.js";
import ManifestInfo from "./ManifestInfo.js";
import SimpleListInfo from "./SimpleListInfo.js";
import { Overview } from "./Overview.js";

import {
  getPermissionsData,
  permissionsColumns,
  permissionsTitle,
} from "./CustomConstants/PermissionsConstants";
import {
  getCodeData,
  codeColumns,
  codeTitle,
} from "./CustomConstants/CodeAnalysisConstants";
import {
  getSharedLibData,
  sharedLibColumns,
  sharedLibTitle,
} from "./CustomConstants/SharedLibraryConstants";
import {
  getNiapData,
  niapColumns,
  niapTitle,
} from "./CustomConstants/NiapConstants";
import {
  getUrlsData,
  urlsColumns,
  urlsTitle,
} from "./CustomConstants/UrlsConstants";
import {
  getLibloomData,
  libloomColumns,
  libloomTitle,
} from "./CustomConstants/LibloomConstants";

import { useTranslation } from "react-i18next";

/**
 * Vista principal del dashboard de una app.
 *
 * Objetivos:
 * - conservar la funcionalidad de main para usuarios anónimos
 * - mantener el responsive y layout limpio
 * - evitar imports duplicados y mezcla de versiones
 */
const useStyles = makeStyles((theme) => ({
  wrapper: {
    width: "100%",
    margin: "0 auto",
  },

  topRow: {
    width: "100%",
    marginTop: theme.spacing(0.5),
  },

  actionsRow: {
    width: "100%",
    margin: theme.spacing(2, 0),
    display: "flex",
    justifyContent: "center",
    alignItems: "center",

    [theme.breakpoints.down("sm")]: {
      margin: theme.spacing(1.5, 0),
    },
  },

  expandedContent: {
    width: "100%",
  },
}));

const DashView = ({ record }) => {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));

  const [showAllCategories, setShowAllCategories] = useState(false);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);

  const { sharedValue } = useGlobal();
  const { id } = sharedValue || {};
  const isLoggedIn = Boolean(id);
  const history = useHistory();

  const { t } = useTranslation("search");

  const handleToggleCategories = () => {
    if (!isLoggedIn) {
      setLoginDialogOpen(true);
      return;
    }

    setShowAllCategories((prev) => !prev);
  };

  const permissionsData = getPermissionsData(record);
  const codeData = getCodeData(record);
  const sharedLibData = getSharedLibData(record);
  const niapData = getNiapData(record);
  const urlsData = getUrlsData(record);
  const libloomData = getLibloomData(record);

  return (
    <Grid className={classes.wrapper}>
      <Dialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        fullScreen={fullScreenDialog}
      >
        <DialogTitle>{t("dashview-login-dialog-title")}</DialogTitle>

        <DialogContent>
          <DialogContentText>
            {t("dashview-login-dialog-text")}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <CoreButton
            onClick={() => setLoginDialogOpen(false)}
            color="default"
          >
            {t("dashview-login-cancel")}
          </CoreButton>

          <CoreButton
            color="primary"
            onClick={() => {
              setLoginDialogOpen(false);
              history.push("/login");
            }}
          >
            {t("dashview-login-submit")}
          </CoreButton>
        </DialogActions>
      </Dialog>

      <Grid container spacing={3} className={classes.topRow}>
        <Grid item xs={12} md={6}>
          <Overview
            permissionsSummary={record?.semaforo?.permisos}
            permissions={record?.permissions}
            color={record?.semaforo?.color}
            infoKey="permpol"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <MetaInfo infoKey="infoapp" />
        </Grid>
      </Grid>

      <div className={classes.actionsRow}>
        <Button
          variant="contained"
          color="customBlue"
          onClick={handleToggleCategories}
        >
          {isLoggedIn
            ? showAllCategories
              ? t("dashview-toggle-hide")
              : t("dashview-toggle-show-full")
            : t("dashview-toggle-show-full")}
        </Button>
      </div>

      {showAllCategories && (
        <div className={classes.expandedContent}>
          <CustomTableInfo
            data={permissionsData}
            columns={permissionsColumns}
            titulo={permissionsTitle}
            infoKey="permisos"
          />

          <CustomTableInfo
            data={libloomData}
            columns={libloomColumns}
            titulo={libloomTitle}
            infoKey="librerias"
          />

          <ManifestInfo record={record} infoKey="manifest" />

          <CustomTableInfo
            data={codeData}
            columns={codeColumns}
            titulo={codeTitle}
            infoKey="codigo"
          />

          <CustomTableInfo
            data={sharedLibData}
            columns={sharedLibColumns}
            titulo={sharedLibTitle}
            infoKey="librerias"
          />

          <CustomTableInfo
            data={niapData}
            columns={niapColumns}
            titulo={niapTitle}
            infoKey="niap"
          />

          <CustomTableInfo
            data={urlsData}
            columns={urlsColumns}
            titulo={urlsTitle}
            infoKey="urls"
          />

          <CertificateInfo record={record} infoKey="certificados" />

          <GridContainer>
            <SimpleListInfo record={record} />
          </GridContainer>
        </div>
      )}
    </Grid>
  );
};

DashView.propTypes = {
  record: PropTypes.object.isRequired,
};

export default DashView;